import { useEffect, useMemo, useState } from "react";

export const PAGE_SIZE = 10;

export function usePagedList(items, resetKey = "", pageSize = PAGE_SIZE) {
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const totalItems = list.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, safePage, pageSize]);

  return {
    page: safePage,
    setPage,
    pageItems,
    totalItems,
    totalPages,
    pageSize,
  };
}

export function TablePagination({ paging, label = "rows" }) {
  if (!paging?.totalItems) return null;
  const { page, totalPages, totalItems, pageSize, setPage } = paging;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  return (
    <div className="mt-3 mx-3 mb-3 flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="text-xs md:text-sm font-medium text-gray-700">
        Showing{" "}
        <span className="font-bold text-gray-900">
          {start}-{end}
        </span>{" "}
        of <span className="font-bold text-red-600">{totalItems}</span> {label}
        {totalPages > 1 && (
          <>
            {" "}
            · page <span className="font-bold">{page}</span> of{" "}
            <span className="font-bold">{totalPages}</span>
          </>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm ${
              page === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            }`}
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm ${
              page === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function Bone({ className = "" }) {
  return <div className={`animate-pulse rounded bg-gray-200 ${className}`} />;
}

export function MetricCardsSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex justify-between">
            <Bone className="h-3 w-24" />
            <Bone className="h-4 w-16 rounded-full" />
          </div>
          <Bone className="h-6 w-20" />
          <Bone className="h-2 w-full" />
          <Bone className="h-2 w-28" />
        </div>
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 6 }) {
  return (
    <div className="overflow-x-auto p-3">
      <div className="space-y-2">
        <div className="flex gap-2 mb-3">
          {Array.from({ length: cols }).map((_, i) => (
            <Bone key={`h-${i}`} className="h-3 flex-1" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex gap-2 py-2 border-t border-gray-50">
            {Array.from({ length: cols }).map((_, c) => (
              <Bone key={`${r}-${c}`} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function BannerSkeleton() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <Bone className="h-4 w-3/4 max-w-xl" />
    </div>
  );
}

export function FormSkeleton() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
      <Bone className="h-5 w-48" />
      <Bone className="h-3 w-72" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Bone className="h-3 w-24" />
            <Bone className="h-10 w-full" />
          </div>
        ))}
      </div>
      <Bone className="h-10 w-40" />
    </div>
  );
}

export function PerformanceCardsSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
          <div className="flex justify-between">
            <div className="space-y-2">
              <Bone className="h-4 w-36" />
              <Bone className="h-3 w-48" />
            </div>
            <Bone className="h-8 w-14" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="rounded-lg bg-gray-50 px-3 py-2 space-y-2">
                <Bone className="h-2 w-20" />
                <Bone className="h-4 w-16" />
              </div>
            ))}
          </div>
          <Bone className="h-3 w-40" />
        </div>
      ))}
    </div>
  );
}

export function PageAccessSkeleton() {
  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-5">
      <div className="space-y-2">
        <Bone className="h-7 w-56" />
        <Bone className="h-4 w-96 max-w-full" />
      </div>
      <div className="flex gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Bone key={i} className="h-10 w-28 rounded-xl" />
        ))}
      </div>
      <MetricCardsSkeleton />
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <Bone className="h-5 w-40" />
        </div>
        <TableSkeleton rows={8} cols={7} />
      </div>
    </div>
  );
}

export function SubTabBtn({ active, onClick, children, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
        active
          ? "text-white border-transparent"
          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
      }`}
      style={active ? { backgroundColor: "#FB565F" } : undefined}
    >
      {children}
      {typeof count === "number" && (
        <span className={`ml-1.5 ${active ? "text-white/80" : "text-gray-400"}`}>({count})</span>
      )}
    </button>
  );
}
